import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form"
import { Input } from "@components/ui/input"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import type { IGetECLAccountParams, IECLResult } from "@apis/shoppers/types"
import { useGetECLAccountQuery } from "@apis/shoppers/useShoppers"

type TSearchAccountNumberFormProps = {
  market_code: string | undefined
  last_name: string | undefined
  onCancel: () => void
  onSelectCustomer: (customer: IECLResult) => void
}

type TSearchFormData = {
  last_name: string
  service_address_line_1: string
  service_zip_code: string
}

export const SearchAccountNumberForm = ({
  market_code,
  last_name,
  onCancel,
  onSelectCustomer,
}: TSearchAccountNumberFormProps) => {
  const form = useForm<TSearchFormData>({
    mode: "onTouched",
    defaultValues: {
      last_name,
    },
  })
  const [searchParams, setSearchParams] = useState<
    IGetECLAccountParams | undefined
  >(undefined)

  const getECLAccountQuery = useGetECLAccountQuery(market_code, searchParams)

  const handleSubmit = (data: TSearchFormData) => {
    setSearchParams({
      n_l: data.last_name,
      s_l1: data.service_address_line_1,
      s_z: data.service_zip_code,
    })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="tw:space-y-6 tw:pt-4"
      >
        <FormField
          control={form.control}
          name="last_name"
          rules={{ required: "Last Name is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter last name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service_address_line_1"
          rules={{ required: "Address is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input placeholder="Enter address" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="service_zip_code"
          rules={{ required: "Zip Code is required" }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Zip Code</FormLabel>
              <FormControl>
                <Input placeholder="Enter zip code" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="tw:flex tw:gap-4">
          <Button type="submit" disabled={!form.formState.isValid}>
            {getECLAccountQuery.isFetching ? (
              <>
                <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
                Searching…
              </>
            ) : (
              "Search"
            )}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>

      <p className="tw:mt-6">
        Verify the last name, zip code, and street address, then click search
      </p>
      {getECLAccountQuery.isFetching && (
        <div className="tw:mt-4 tw:flex tw:justify-center">
          <Loader2 className="tw:h-10 tw:w-10 tw:animate-spin" />
        </div>
      )}

      {!getECLAccountQuery.isPending &&
        getECLAccountQuery?.data &&
        getECLAccountQuery?.data?.length > 0 && (
          <div className="tw:mt-6 tw:space-y-4">
            {getECLAccountQuery?.data?.map((customer) => (
              <div
                key={customer.account_number}
                className="hover:tw:bg-muted tw:cursor-pointer tw:rounded-md tw:border tw:p-4 tw:transition"
                onClick={() => onSelectCustomer(customer)}
              >
                <h5 className="tw:font-semibold">{customer.customer_name_1}</h5>
                <h6 className="tw:text-sm tw:text-muted-foreground">
                  {customer.account_number}
                </h6>
                <p className="tw:text-sm">
                  {customer.service_address_1} {customer.service_address_2}
                  <br />
                  {customer.service_city}, {customer.service_state}{" "}
                  {customer.service_zip_code}
                </p>
              </div>
            ))}
          </div>
        )}
    </Form>
  )
}
